import React from 'react';
import {Box, Text, Avatar, Link} from '@primer/react';
import {
    BriefcaseIcon,
    OrganizationIcon,
    CalendarIcon,
    LocationIcon
} from '@primer/octicons-react';
import timelineData from '../data/timelineData';

const Timeline = ({ filter = [], onFilterChange }) => {
    // Convert single string filter to array for backward compatibility
    const activeFilters = Array.isArray(filter) ? filter : [filter];

    // If no filters are selected, show all categories
    const shouldShowAll = activeFilters.length === 0;

    // Filter timeline items based on the selected filters
    const filteredItems = shouldShowAll
        ? timelineData
        : timelineData.filter(item => activeFilters.includes(item.category));

    // Get category icon
    const getCategoryIcon = (category) => {
        switch(category) {
            case 'work':
                return BriefcaseIcon;
            case 'internship':
                return OrganizationIcon;
            default:
                return BriefcaseIcon;
        }
    };

    // Get category border color
    const getCategoryBorderColor = (category) => {
        switch(category) {
            case 'work':
                return 'success.muted';
            case 'internship':
                return 'accent.muted';
            default:
                return 'border.default';
        }
    };

    // Get node size based on category - using responsive array format
    const getNodeSize = (category) => {
        switch(category) {
            case 'work':
                return [24, 42, 56]; // [mobile, tablet, desktop] - Much smaller on mobile
            case 'internship':
                return [20, 36, 48];
            default:
                return [20, 36, 48];
        }
    };

    // Use image_url from data or fallback to a default
    const getCompanyLogo = (item) => {
        if (item.image_url) {
            return item.image_url;
        }
        // Fallback to default icon
        return null;
    };

    // Format date display
    const formatDate = (dateString) => {
        if (!dateString || dateString === 'Present') return 'Present';
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short' }).format(date);
    };

    // Get content width based on category - responsive arrays
    const getContentWidth = (category) => {
        switch(category) {
            case 'work':
                return ['calc(100% - 4px)', 'calc(100% - 12px)', '98%']; // [mobile, tablet, desktop] - less margin on mobile
            case 'internship':
                return ['calc(100% - 4px)', 'calc(100% - 12px)', '85%'];
            default:
                return ['calc(100% - 4px)', 'calc(100% - 12px)', '98%'];
        }
    };

    return (
        <Box>
          <Box
                    bg="accent.subtle"
                    p={4}
                    borderRadius={2}
                    border="1px solid"
                    borderColor="accent.muted"
                    mb={4}
                >
          <Link target='_blank' href="https://www.linkedin.com/in/dylancattelan" sx={{ color: 'accent.fg' }}>
                        <Text
                            sx={{
                                fontSize: [1, 2],
                                color: 'accent.fg',
                                lineHeight: 1.5
                            }}
                        >
                            For an up to date and cleaner version, feel free to go on my <b>LinkedIn profile</b> directly.
                    </Text>
                    </Link>
                </Box>
            

            {/* Filter buttons */}
            <Box
                display="flex"
                flexDirection={["column", "row"]}
                alignItems={["flex-start", "center"]}
                mb={4}
                gap={2}
            >
                <Text fontWeight="bold" fontSize={2} mr={2} color="fg.muted">Filters: </Text>
                <Box
                    display="flex"
                    flexWrap="wrap"
                    gap={2}
                >
                    <Box
                        as="button"
                        onClick={() => {
                            // Toggle the 'work' filter
                            const newFilters = activeFilters.includes('work')
                                ? activeFilters.filter(f => f !== 'work')
                                : [...activeFilters, 'work'];
                            onFilterChange(newFilters);
                        }}
                        sx={{
                            px: 2,
                            py: 1,
                            borderRadius: 2,
                            fontSize: 1,
                            fontWeight: activeFilters.includes('work') ? 'bold' : 'normal',
                            bg: activeFilters.includes('work') ? 'success.subtle' : 'canvas.subtle',
                            border: '1px solid',
                            borderColor: activeFilters.includes('work') ? 'success.muted' : 'border.default',
                            color: activeFilters.includes('work') ? 'success.fg' : 'fg.muted',
                            cursor: 'pointer',
                            textDecoration: activeFilters.includes('work') ? 'none' : 'line-through',
                            opacity: activeFilters.includes('work') ? 1 : 0.7,
                            '&:hover': {
                                bg: 'canvas.inset',
                                borderColor: 'success.muted',
                            }
                        }}
                    >
                        Work
                    </Box>
                    <Box
                        as="button"
                        onClick={() => {
                            // Toggle the 'internship' filter
                            const newFilters = activeFilters.includes('internship')
                                ? activeFilters.filter(f => f !== 'internship')
                                : [...activeFilters, 'internship'];
                            onFilterChange(newFilters);
                        }}
                        sx={{
                            px: 2,
                            py: 1,
                            borderRadius: 2,
                            fontSize: 1,
                            fontWeight: activeFilters.includes('internship') ? 'bold' : 'normal',
                            bg: activeFilters.includes('internship') ? 'accent.subtle' : 'canvas.subtle',
                            border: '1px solid',
                            borderColor: activeFilters.includes('internship') ? 'accent.muted' : 'border.default',
                            color: activeFilters.includes('internship') ? 'accent.fg' : 'fg.muted',
                            cursor: 'pointer',
                            textDecoration: activeFilters.includes('internship') ? 'none' : 'line-through',
                            opacity: activeFilters.includes('internship') ? 1 : 0.7,
                            '&:hover': {
                                bg: 'canvas.inset',
                                borderColor: 'accent.muted',
                            }
                        }}
                    >
                        Internship
                    </Box>
                </Box>
            </Box>

            {/* Timeline with vertical line */}
            <Box
                className="timeline-container"
                sx={{
                    position: 'relative',
                    display: 'grid',
                    gridTemplateColumns: ['44px 1fr', '60px 1fr', '76px 1fr'], // Slightly larger mobile column for better text flow
                    width: '100%',
                    overflowX: 'hidden', // Prevent horizontal scrolling
                    px: [0, 0, 0], // No extra padding that could cause overflow
                    maxWidth: '100%', // Ensure it doesn't exceed container
                    gap: [0.5, 2, 2] // Smaller gap on mobile
                }}
            >
                {/* The vertical timeline line - centered in first column */}
                <Box
                    sx={{
                        position: 'absolute',
                        width: '2px',
                        backgroundColor: 'border.default',
                        top: 0,
                        bottom: 0,
                        left: ['18px', '30px', '38px'], // Centered for smaller avatars on mobile (36px/2 = 18px)
                        zIndex: 1
                    }}
                />

                {/* Timeline items */}
                {filteredItems.map((item) => {
                    return (
                        <React.Fragment key={item.id}>
                            {/* Circle/node column */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    paddingTop: ['12px', '20px', '32px'], // Less padding on mobile
                                    position: 'relative',
                                    zIndex: 2
                                }}
                            >
                                {getCompanyLogo(item) ? (
                                    <Avatar
                                        src={getCompanyLogo(item)}
                                        size={getNodeSize(item.category)}
                                        sx={{
                                            borderRadius: '50%',
                                            border: '2px solid',
                                            borderColor: 'white',
                                            boxShadow: '0 0 0 2px rgba(234, 238, 242, 0.5)',
                                            bg: 'white'
                                        }}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            width: getNodeSize(item.category),
                                            height: getNodeSize(item.category),
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: 'canvas.subtle',
                                            border: '2px solid white',
                                            color: 'fg.subtle',
                                            boxShadow: '0 0 0 2px rgba(234, 238, 242, 0.5)'
                                        }}
                                    >
                                        {React.createElement(getCategoryIcon(item.category), {
                                            size: [10, 18, 28] // Responsive icon sizes [mobile, tablet, desktop] - smaller on mobile
                                        })}
                                    </Box>
                                )}
                            </Box>

                            {/* Content column */}
                            <Box
                                className="timeline-content"
                                sx={{
                                    padding: ['4px 0', '6px 0', '8px 0'],
                                    paddingBottom: ['16px', '20px', item.category === 'work' ? 6 : 5], // Less spacing on mobile
                                    position: 'relative',
                                    width: '100%', // Ensure the content doesn't exceed container width
                                    pr: [1, 2, 0], // Less right padding on mobile
                                    minWidth: 0, // Allow flex items to shrink below their minimum content size
                                    pl: [0, 0, 0] // No left padding to maximize space
                                }}
                            >
                                <Box
                                    sx={{
                                        border: '2px solid',
                                        borderColor: getCategoryBorderColor(item.category),
                                        borderRadius: 2,
                                        padding: [2, 2, 3],
                                        backgroundColor: 'canvas.subtle',
                                        width: getContentWidth(item.category), // Responsive width
                                        maxWidth: '100%', // Ensure content doesn't overflow
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
                                        boxSizing: 'border-box' // Include padding in the width calculation
                                    }}
                                >
                                    {/* Date and location chips at the top inside the card */}
                                    <Box
                                        display="flex"
                                        flexDirection={["column", "row"]}
                                        flexWrap="wrap"
                                        gap={[1, 2]}
                                        mb={3}
                                        className="timeline-meta"
                                    >
                                        <Box
                                            className="chip"
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                fontSize: 0,
                                                color: 'fg.muted',
                                                bg: 'canvas.default',
                                                border: '1px solid',
                                                borderColor: 'border.muted',
                                                borderRadius: '2em',
                                                px: [1, 1, 2],
                                                py: 1,
                                                mr: [0, 1, 2],
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            <CalendarIcon size={12} style={{ marginRight: '6px' }} />
                                            {formatDate(item.startDate)} — {formatDate(item.endDate)}
                                        </Box>
                                        <Box
                                            className="timeline-location chip"
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                fontSize: 0,
                                                color: 'fg.muted',
                                                bg: 'canvas.default',
                                                border: '1px solid',
                                                borderColor: 'border.muted',
                                                borderRadius: '2em',
                                                px: [1, 1, 2],
                                                py: 1,
                                                mr: [0, 1, 2],
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                                whiteSpace: 'nowrap',
                                                maxWidth: ['100%', '100%', 'auto'],
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}
                                        >
                                            <LocationIcon size={12} style={{ marginRight: '6px', flexShrink: 0 }} />
                                            <Text
                                                sx={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {item.location.split('·')[0].trim()}
                                            </Text>
                                        </Box>
                                        {item.isRemote && (
                                            <Box
                                                className="chip badge"
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    fontSize: 0,
                                                    color: 'accent.fg',
                                                    bg: 'accent.subtle',
                                                    border: '1px solid',
                                                    borderColor: 'accent.muted',
                                                    borderRadius: '2em',
                                                    px: [1, 1, 2],
                                                    py: 1,
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                                    fontWeight: 500,
                                                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif'
                                                }}
                                            >
                                                Remote
                                            </Box>
                                        )}
                                        {item.isHybrid && (
                                            <Box
                                                className="chip badge"
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    fontSize: 0,
                                                    color: '#d97706',
                                                    bg: '#fed7aa',
                                                    border: '1px solid',
                                                    borderColor: '#fb923c',
                                                    borderRadius: '2em',
                                                    px: [1, 1, 2],
                                                    py: 1,
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                                    fontWeight: 500,
                                                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif'
                                                }}
                                            >
                                                Hybrid
                                            </Box>
                                        )}
                                    </Box>

                                    <Box mb={1}>
                                        <Text
                                            as="h3"
                                            sx={{
                                                fontSize: [2, 2, 3],
                                                fontWeight: 'bold',
                                                color: 'fg.default',
                                                mb: 1
                                            }}
                                        >
                                            {item.title}
                                        </Text>
                                        <Text
                                            as="h4"
                                            sx={{
                                                fontSize: [1, 1, 2],
                                                fontWeight: 'medium',
                                                color: 'fg.muted'
                                            }}
                                        >
                                            {item.company}
                                        </Text>
                                    </Box>

                                    {/* Display description for all items */}
                                    {item.description && (
                                        <Text
                                            as="p"
                                            mb={item.skills && item.skills.length > 0 ? 3 : 0}
                                            sx={{
                                                fontSize: item.category === 'internship' ? 1 : [1, 1, 2],
                                                color: 'fg.muted',
                                                lineHeight: 1.5,
                                                marginTop: 2,
                                                letterSpacing: '0.01em'
                                            }}
                                        >
                                            {item.category === 'internship'
                                                ? item.description.split('\n')[0] // Only show first line for internships
                                                : item.description}
                                        </Text>
                                    )}

                                    {/* Skills as bullet points for work and internships - show all skills */}
                                    {item.skills && item.skills.length > 0 && (
                                        <Box
                                            as="ul"
                                            marginTop={3}
                                            marginLeft={[2, 2, 3]}
                                            sx={{
                                                listStyle: 'disc',
                                                fontSize: [1, 1, item.category === 'internship' ? 1 : 2],
                                                lineHeight: 1.5,
                                                color: 'fg.muted',
                                                '& > li': {
                                                    marginBottom: 2,
                                                    fontWeight: item.category === 'work' ? 400 : 300,
                                                }
                                            }}
                                        >
                                            {item.skills.map((skill, index) => (
                                                <Box as="li" key={index} mb={1}>
                                                    {skill}
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </React.Fragment>
                    )})}
            </Box>
        </Box>
    );
};

export default Timeline;